import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useLiveHandshakesStore } from '@/stores/liveHandshakes.store';
import { clockHandshakeParams } from '@/views/main/clock/utils/clock-handshake';
import {
  createHandshakeGameSuggestion,
  formatGamePlayerLabel,
  passerbyDisplayName,
  type HandshakeGamePlayer,
} from '@/views/main/handshakes/utils/handshake-game';

/**
 * The two people a live handshake seats, the signed-in player first. A passerby
 * keeps the empty portrait and the code, the same names the clock half shows.
 */
export function playersForLiveHandshake(
  row: LiveHandshakeDto,
  me: { id: string; username: string },
  passerbyName: string,
): [HandshakeGamePlayer, HandshakeGamePlayer] {
  const other: HandshakeGamePlayer = row.peer
    ? {
        id: row.peer.id,
        username: row.peer.username,
        role: row.initiatorId === row.peer.id ? 'initiator' : 'responder',
        image: row.peer.profileImageBlobHash,
      }
    : {
        id: `${row.id}:passerby`,
        username: passerbyDisplayName(passerbyName, row.passerbyClaimCode, true),
        role: 'responder',
        isPasserby: true,
      };

  return [
    {
      id: me.id,
      username: me.username,
      role: row.initiatorId === me.id ? 'initiator' : 'responder',
    },
    other,
  ];
}

/**
 * Opens the clock on this handshake, on top of whatever is showing, so closing
 * it returns there. The recommendation is the clock itself.
 */
export function pushHandshakeClock(input: {
  handshakeId: string;
  players: [HandshakeGamePlayer, HandshakeGamePlayer];
  round?: number;
  suggestionSeed?: string | null;
  initiatorPlaysWhite?: boolean | null;
  minutes?: number | null;
  increment?: number | null;
  clockChosen?: boolean;
}): void {
  const meId = useAuthStore().user?.id;
  if (!meId) return;

  const me = input.players.find((player) => player.id === meId);
  const them = me && input.players.find((player) => player.id !== me.id);
  if (!me || !them) return;

  const round = input.round ?? 0;
  const suggestion = createHandshakeGameSuggestion(
    input.suggestionSeed || input.handshakeId,
    input.players,
    round,
    null,
    {
      initiatorPlaysWhite: input.initiatorPlaysWhite,
      clockChosen: input.clockChosen,
      minutes: input.minutes,
      increment: input.increment,
    },
  );

  // The router owns the views that call this, so it is loaded only when a tap asks for the clock.
  void import('@/router').then(({ default: appRouter }) => {
    void appRouter.push({
      name: RouterNameType.CLOCK,
      query: {
        min: String(suggestion.minutes),
        inc: String(suggestion.increment),
        near: formatGamePlayerLabel(me),
        far: formatGamePlayerLabel(them),
        side: suggestion.white.id === me.id ? 'w' : 'b',
        ...clockHandshakeParams(input.handshakeId, me, them, round, input.suggestionSeed || undefined),
      },
    });
  });
}

/** The clock for a handshake the live store already has. Nothing, when it does not. */
export async function openLiveHandshakeClock(handshakeId: string, passerbyName: string): Promise<void> {
  const auth = useAuthStore();
  const store = useLiveHandshakesStore();
  await store.ensure();
  if (!store.byId(handshakeId)) await store.load();

  const row = store.byId(handshakeId);
  const meId = auth.user?.id;
  const username = auth.userDetails?.username;
  if (!row || !meId || !username) return;

  pushHandshakeClock({
    handshakeId: row.id,
    players: playersForLiveHandshake(row, { id: meId, username }, passerbyName),
    round: row.suggestionRound,
    suggestionSeed: row.suggestionSeed,
    initiatorPlaysWhite: row.suggestionInitiatorWhite,
    minutes: row.suggestionMinutes,
    increment: row.suggestionIncrement,
    clockChosen: row.suggestionClockChosen,
  });
}
